import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { User, Phone, Building, GraduationCap, Linkedin, Upload, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { authAPI, uploadAPI, resumeAnalysisAPI } from "../../services/api";
import clarityService from "../../services/clarityService";
import ResumeAnalysisBlurredPreview, {
  ResumePreviewAnalysis,
  suggestSkillsFromAnalysis,
  suggestProfileFromAnalysis,
} from "@/components/profile/ResumeAnalysisBlurredPreview";
import { lookupIndiaPincode } from "@/utils/indiaPincodeLookup";


export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState<"aspirant" | "mentor" | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEmailNotVerified, setIsEmailNotVerified] = useState(false);
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  // Members may sign up with LinkedIn or with an email + password.
  const [usePassword, setUsePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [linkedinProfileUrl, setLinkedinProfileUrl] = useState("");
  const [resumePreviewAnalyzing, setResumePreviewAnalyzing] = useState(false);
  const [resumePreviewError, setResumePreviewError] = useState("");
  const [resumePreview, setResumePreview] = useState<ResumePreviewAnalysis | null>(null);
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const [pincodeLookingUp, setPincodeLookingUp] = useState(false);
  const [pincodeLookupFailed, setPincodeLookupFailed] = useState(false);

  // Track form start when component mounts
  useEffect(() => {
    clarityService.trackFormInteraction('registration', 'start');
  }, []);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    addressLine1: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    resume: null as File | null,
    primarySkills: "",
    secondarySkills: "",
    dateOfBirth: "",
    gender: "" as "" | "male" | "female",
    maritalStatus: "" as "" | "married" | "unmarried",
    lookingForJob: false,
    upskilling: false,
    justCommunityMember: false,
    password: "",
    confirmPassword: "",
  });
  
  const [fieldErrors, setFieldErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    addressLine1: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    resume: "",
    primarySkills: "",
    secondarySkills: "",
    dateOfBirth: "",
    gender: "",
    maritalStatus: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const loadLinkedInPending = async () => {
      const urlError = searchParams.get("error");
      if (urlError === "not_registered") {
        setError("No account found. Please register to create your Udyoga Pramoda account.");
      } else if (urlError === "linkedin_already_registered") {
        setError("This LinkedIn account is already registered. Please sign in instead.");
      }

      if (searchParams.get("linkedin") === "connected") {
        try {
          const response = await authAPI.getLinkedInPending();
          if (response.success && response.data) {
            const data = response.data;
            setLinkedinConnected(true);
            setLinkedinProfileUrl(data.linkedinProfile || "");
            setFormData((prev) => ({
              ...prev,
              email: data.email || prev.email,
              firstName: data.firstName || prev.firstName,
              lastName: data.lastName || prev.lastName,
            }));
          }
        } catch {
          setError("LinkedIn session expired. Please connect your LinkedIn account again.");
        }
      }
    };

    loadLinkedInPending();
  }, [searchParams]);

  useEffect(() => {
    const pin = formData.pincode.replace(/\D/g, "");
    if (pin.length !== 6) {
      setPincodeLookupFailed(false);
      return;
    }

    let cancelled = false;
    setPincodeLookingUp(true);
    setPincodeLookupFailed(false);
    lookupIndiaPincode(pin).then((result) => {
      if (cancelled) return;
      if (result) {
        setFormData((prev) => ({
          ...prev,
          city: result.city,
          state: result.state,
          country: result.country,
        }));
        setPincodeLookupFailed(false);
      } else {
        setPincodeLookupFailed(true);
      }
    }).finally(() => {
      if (!cancelled) setPincodeLookingUp(false);
    });

    return () => {
      cancelled = true;
    };
  }, [formData.pincode]);

  // Field validation functions
  const validateField = (name: string, value: string | File | null | boolean) => {
    let error = "";
    
    switch (name) {
      case "firstName":
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          error = "First name is required";
        } else if (typeof value === 'string' && !/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = "First name should contain only alphabets";
        } else if (typeof value === 'string' && value.trim().length < 2) {
          error = "First name must be at least 2 characters";
        }
        break;
      case "lastName":
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          error = "Last name is required";
        } else if (typeof value === 'string' && !/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = "Last name should contain only alphabets";
        } else if (typeof value === 'string' && value.trim().length < 2) {
          error = "Last name must be at least 2 characters";
        }
        break;
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value || (typeof value === 'string' && !emailRegex.test(value))) {
          error = "Please enter a valid email address";
        }
        break;
      case "phone":
        if (!value || (typeof value === 'string' && !/^[\+]?[1-9][\d]{0,15}$/.test(value.trim()))) {
          error = "Please provide a valid phone number";
        }
        break;
      case "addressLine1":
        if (!value || (typeof value === 'string' && value.trim().length < 5)) {
          error = "Address must be at least 5 characters";
        }
        break;
      case "city":
        if (!value || (typeof value === 'string' && value.trim().length < 2)) {
          error = "City must be at least 2 characters";
        }
        break;
      case "state":
        if (!value || (typeof value === 'string' && value.trim().length < 2)) {
          error = "State must be at least 2 characters";
        }
        break;
      case "country":
        if (!value || (typeof value === 'string' && value.trim().length < 2)) {
          error = "Country must be at least 2 characters";
        }
        break;
      case "pincode":
        if (!value || (typeof value === 'string' && !/^\d{6}$/.test(value.trim()))) {
          error = "Pincode must be 6 digits";
        }
        break;
      case "resume":
        if (selectedRole === "aspirant" && !value) {
          error = "Resume is required for aspirants";
        }
        break;
      case "primarySkills":
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          error = "At least one primary skill is required";
        }
        break;
      case "dateOfBirth":
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          error = "Date of birth is required";
        }
        break;
      case "gender":
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          error = "Gender is required";
        }
        break;
      case "maritalStatus":
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          error = "Marital status is required";
        }
        break;
    }
    
    setFieldErrors(prev => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleFieldBlur = (name: string, value: string | File | null | boolean) => {
    validateField(name, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!linkedinConnected && !usePassword) {
      setError("Connect LinkedIn or choose an email and password to register.");
      return;
    }

    if (usePassword && !linkedinConnected) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setError("Please enter a valid email address.");
        return;
      }
      if (formData.password.length < 8) {
        setError("Password must be at least 8 characters long.");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    }
    
    if (!selectedRole) {
      setError("Please select your role");
      return;
    }

    // For aspirants, validate that at least one "What brings you here?" option is selected
    if (selectedRole === "aspirant") {
      if (!formData.lookingForJob && !formData.upskilling && !formData.justCommunityMember) {
        setError("Please select at least one option for 'What brings you here?'");
        return;
      }
    }

    // Validate all fields
    const fields: [string, string | File | null | boolean][] = [
      ["firstName", formData.firstName],
      ["lastName", formData.lastName],
      ["email", formData.email],
      ["phone", formData.phone],
      ["addressLine1", formData.addressLine1],
      ["city", formData.city],
      ["state", formData.state],
      ["country", formData.country],
      ["pincode", formData.pincode],
      ["primarySkills", formData.primarySkills],
      ["dateOfBirth", formData.dateOfBirth],
      ["gender", formData.gender],
      ["maritalStatus", formData.maritalStatus],
    ];

    if (selectedRole === "aspirant") {
      fields.push(["resume", formData.resume]);
    }

    let hasErrors = false;
    fields.forEach(([name, value]) => {
      if (!validateField(name, value)) {
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setError("Please fix the errors above");
      return;
    }

    if (!formData.addressLine1.trim() || !formData.city.trim() || !formData.state.trim() || !formData.country.trim() || !formData.pincode.trim()) {
      setError("Address, City, State, Country, and Pincode are all required");
      return;
    }

    if (selectedRole === "aspirant" && !formData.resume) {
      setError("Resume is required for aspirants");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    // Track registration start with Clarity
    clarityService.trackUserRegistration(selectedRole, 'form_submission');

    // Which half of this is running. The upload and the account creation used
    // to share one catch, so a failed upload was reported as a failed
    // registration and neither the person nor we could tell which had broken.
    let step: 'upload' | 'register' = 'register';

    try {
      let resumeUrl = "";
      let resumeMetadata = null;
      
      // Upload resume first (only for aspirants)
      if (selectedRole === "aspirant" && formData.resume) {
        step = 'upload';
        setSuccess("Uploading resume...");
        const uploadResponse = await uploadAPI.uploadResume(formData.resume);
        resumeUrl = uploadResponse.data.resumeUrl;
        
        // Store metadata for registration
        resumeMetadata = {
          originalName: uploadResponse.data.uploadDetails.originalName,
          uploadedAt: uploadResponse.data.uploadDetails.uploadedAt,
          fileSize: formData.resume.size,
          s3Key: uploadResponse.data.uploadDetails.key
        };
        
        // Show detailed upload information
        console.log('📄 Upload Details:', {
          resumeUrl: resumeUrl,
          uploadDetails: uploadResponse.data.uploadDetails,
          resumeMetadata: resumeMetadata,
          fileName: formData.resume.name,
          fileSize: formData.resume.size
        });
        
        const uploadDetails = uploadResponse.data.uploadDetails;
        setSuccess(
          `✅ Resume uploaded successfully! 
          File: ${uploadDetails.originalName} 
          Verified: ${uploadDetails.verified ? 'Yes' : 'No'}
          Creating account...`
        );
      }
      
      // Parse skills from comma-separated strings
      const primarySkillsArray = formData.primarySkills.split(',').map(skill => skill.trim()).filter(skill => skill);
      const secondarySkillsArray = formData.secondarySkills.split(',').map(skill => skill.trim()).filter(skill => skill);
      
      // Create full name from first and last name
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
      
      const registrationData: any = {
        fullName: fullName, // Send fullName instead of firstName/lastName
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        address: `${formData.addressLine1.trim()}, ${formData.city.trim()}, ${formData.state.trim()}, ${formData.country.trim()}, ${formData.pincode.trim()}`,
        resumeUrl: resumeUrl, // Real S3 URL (only for aspirants)
        resumeMetadata: resumeMetadata, // File metadata (only for aspirants)
        primarySkills: primarySkillsArray,
        secondarySkills: secondarySkillsArray,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        maritalStatus: formData.maritalStatus,
        role: selectedRole
      };

      // Credential signup: send the chosen password so the account can sign in
      // without LinkedIn. The server hashes it before storing.
      if (usePassword && !linkedinConnected) {
        registrationData.password = formData.password;
      }

      // Add interest fields for aspirants
      if (selectedRole === 'aspirant') {
        registrationData.lookingForJob = formData.lookingForJob;
        registrationData.upskilling = formData.upskilling;
        registrationData.justCommunityMember = formData.justCommunityMember;
      }

      step = 'register';
      const response = await authAPI.register(registrationData);

      // Track successful registration with Clarity
      clarityService.trackUserRegistration(selectedRole, 'registration_completed');

      const devOtp = response.data?.devOtp;

      setSuccess(
        devOtp
          ? `Registration successful! Email could not be sent — use dev code: ${devOtp}`
          : "Registration successful! Please check your email for the verification code."
      );

      setTimeout(() => {
        navigate("/verify-otp", {
          state: {
            email: formData.email.trim().toLowerCase(),
            devOtp,
          },
        });
      }, 2000);

    } catch (err: any) {
      console.error("Registration error:", err);
      // Track registration failure with Clarity
      clarityService.trackUserRegistration(selectedRole, 'form_failure');

      if (err.response && err.response.status === 413) {
        const tooBig = err.response.data?.message || "That file is too large. The limit is 10MB.";
        setError(tooBig);
        setFieldErrors(prev => ({ ...prev, resume: tooBig }));
      } else if (err.response?.data?.message) {
        // The server explained itself, so pass that on rather than guessing.
        setError(err.response.data.message);
        setIsEmailNotVerified(err.response?.data?.code === 'EMAIL_NOT_VERIFIED');
      } else if (!err.response) {
        // Nothing came back: the request never completed. Usually the
        // connection, and the upload is the long one that suffers for it.
        setError(
          step === 'upload'
            ? "Your resume could not be uploaded — the connection dropped before it finished. " +
              "Check your internet and try again. A smaller file, or a WiFi connection, usually helps."
            : "We could not reach the server to create your account. Check your internet and try again."
        );
        if (step === 'upload') {
          setFieldErrors(prev => ({ ...prev, resume: 'Upload did not finish. Try again.' }));
        }
      } else {
        setError(
          step === 'upload'
            ? `Your resume could not be uploaded (error ${err.response.status}). Please try again.`
            : `Your account could not be created (error ${err.response.status}). Please try again.`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear errors when user starts typing
    if (error) {
      setError('');
      setIsEmailNotVerified(false);
    }
  };



  const handleRoleSelect = (role: "aspirant" | "mentor") => {
    setSelectedRole(role);
    if (error) {
      setError('');
      setIsEmailNotVerified(false);
    }
    
    // Track role selection with Clarity
    clarityService.trackFormInteraction('registration', 'complete', 'role_selection');
    clarityService.setCustomMetric('selected_role', role);
  };

  const runResumePreview = async (file: File) => {
    const isPdf = file.type.includes('pdf') || file.name.toLowerCase().endsWith('.pdf');
    const isDocx =
      file.type.includes('wordprocessingml') || file.name.toLowerCase().endsWith('.docx');

    if (!isPdf && !isDocx) {
      setResumePreview(null);
      setResumePreviewError('');
      setResumePreviewAnalyzing(false);
      return;
    }

    setResumePreviewAnalyzing(true);
    setResumePreviewError('');
    setResumePreview(null);

    try {
      const response = await resumeAnalysisAPI.preview(file);
      if (response.success) {
        const analysis = response.data.analysis as ResumePreviewAnalysis;
        setResumePreview(analysis);
        const suggested = suggestSkillsFromAnalysis(analysis);
        const profile = suggestProfileFromAnalysis(analysis);
        setFormData((prev) => ({
          ...prev,
          primarySkills: suggested.primary || prev.primarySkills,
          secondarySkills: suggested.secondary || prev.secondarySkills,
          phone: prev.phone || profile.phone || prev.phone,
          addressLine1: prev.addressLine1 || profile.addressLine1 || prev.addressLine1,
          city: prev.city || profile.city || prev.city,
          state: prev.state || profile.state || prev.state,
          country: prev.country || profile.country || prev.country,
          pincode: prev.pincode || profile.pincode || prev.pincode,
          dateOfBirth: prev.dateOfBirth || profile.dateOfBirth || prev.dateOfBirth,
          gender: prev.gender || profile.gender || prev.gender,
          maritalStatus: prev.maritalStatus || profile.maritalStatus || prev.maritalStatus,
        }));
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not preview resume analysis. You can still complete registration.';
      setResumePreviewError(message);
    } finally {
      setResumePreviewAnalyzing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.includes('pdf') && !file.type.includes('doc') && !file.type.includes('docx')) {
        setError('Please upload a PDF, DOC, or DOCX file');
        return;
      }
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        resume: file,
      }));
      if (error) {
        setError('');
        setIsEmailNotVerified(false);
      }
      runResumePreview(file);
    }
  };

  const handleLinkedInConnect = () => {
    window.location.href = '/api/auth/linkedin?mode=register';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-6 sm:py-8">
      <div className="w-full max-w-lg space-y-6 sm:space-y-8">
        {/* Header Section */}
        <div className="text-center space-y-2">
          <h2 className="text-xl sm:text-2xl font-bold text-secondary mb-2">
            Join Udyoga Pramoda
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            Create your account and start your career journey
          </p>
        </div>

        {/* Registration Form Card */}
        <Card className="border-border shadow-lg">
          <CardContent className="space-y-6 pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* LinkedIn Connect (required) */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-secondary">How would you like to sign up? *</Label>
                <p className="text-xs text-gray-500">
                  Connect LinkedIn to prefill your details, or create an account with an email and password. Use the same method when you sign in later.
                </p>
                {linkedinConnected ? (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-2">
                    <div className="flex items-center gap-2 text-green-700">
                      <Linkedin className="h-5 w-5" />
                      <span className="font-medium">LinkedIn account connected</span>
                    </div>
                    {linkedinProfileUrl && (
                      <p className="text-sm text-green-700 break-all">{linkedinProfileUrl}</p>
                    )}
                    <p className="text-sm text-green-700">{formData.email}</p>
                  </div>
                ) : (
                  <Button
                    type="button"
                    onClick={handleLinkedInConnect}
                    className="w-full h-11 bg-[#0A66C2] hover:bg-[#004182] text-white"
                  >
                    <Linkedin className="h-4 w-4 mr-2" />
                    Connect with LinkedIn
                  </Button>
                )}
                {!linkedinConnected && (
                  <>
                    <div className="relative py-1">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase tracking-wider">
                        <span className="bg-white px-2 text-gray-500">or</span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setUsePassword((v) => !v)}
                      className="w-full h-11"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      {usePassword ? "Hide email sign-up" : "Sign up with email and password"}
                    </Button>
                  </>
                )}

                {!linkedinConnected && !usePassword && (
                  <p className="text-xs text-amber-600">
                    Choose one of the options above to continue.
                  </p>
                )}
              </div>

              {/* Email + password, for members not using LinkedIn */}
              {!linkedinConnected && usePassword && (
                <div className="space-y-4 rounded-lg border border-border p-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-secondary">
                      Email *
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={(e) => handleFieldBlur("email", e.target.value)}
                        className="pl-10 h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                        required
                        autoComplete="email"
                      />
                    </div>
                    {fieldErrors.email && <p className="text-red-500 text-sm">{fieldErrors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium text-secondary">
                      Password *
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="At least 8 characters"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="pl-10 pr-10 h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium text-secondary">
                      Confirm Password *
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Re-enter your password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="pl-10 h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                        required
                        autoComplete="new-password"
                      />
                    </div>
                    {formData.confirmPassword.length > 0 &&
                      formData.password !== formData.confirmPassword && (
                        <p className="text-red-500 text-sm">Passwords do not match</p>
                      )}
                  </div>
                </div>
              )}

              {/* Name — from LinkedIn, or typed for a credential signup */}
              {(linkedinConnected || usePassword) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-medium text-secondary">
                      First Name *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        placeholder="First name"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        onBlur={(e) => handleFieldBlur("firstName", e.target.value)}
                        className="pl-10 h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                        required
                      />
                      {fieldErrors.firstName && <p className="text-red-500 text-sm mt-1">{fieldErrors.firstName}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-medium text-secondary">
                      Last Name *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        placeholder="Last name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        onBlur={(e) => handleFieldBlur("lastName", e.target.value)}
                        className="pl-10 h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                        required
                      />
                      {fieldErrors.lastName && <p className="text-red-500 text-sm mt-1">{fieldErrors.lastName}</p>}
                    </div>
                  </div>
                  <p className="col-span-2 text-xs text-gray-500">
                    {linkedinConnected ? "Pre-filled from LinkedIn — edit if needed." : "Enter your name as you'd like it shown."}
                  </p>
                </div>
              )}

              {/* Role Selection */}
              <div className={`space-y-3 ${!linkedinConnected && !usePassword ? "opacity-50 pointer-events-none" : ""}`}>
                <Label className="text-sm font-medium text-secondary">I am a:</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleRoleSelect("aspirant")}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                      selectedRole === "aspirant"
                        ? "border-primary bg-primary-light"
                        : "border-border hover:border-primary hover:bg-background"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <GraduationCap
                        className={`h-5 w-5 shrink-0 ${
                          selectedRole === "aspirant" ? "text-primary" : "text-secondary"
                        }`}
                      />
                      <span
                        className={`font-medium text-sm ${
                          selectedRole === "aspirant" ? "text-primary" : "text-secondary"
                        }`}
                      >
                        Career Aspirant
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 ml-7">
                      Student, job seeker, or career changer
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRoleSelect("mentor")}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                      selectedRole === "mentor"
                        ? "border-primary bg-primary-light"
                        : "border-border hover:border-primary hover:bg-background"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Building
                        className={`h-5 w-5 shrink-0 ${
                          selectedRole === "mentor" ? "text-primary" : "text-secondary"
                        }`}
                      />
                      <span
                        className={`font-medium text-sm ${
                          selectedRole === "mentor" ? "text-primary" : "text-secondary"
                        }`}
                      >
                        Mentor
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 ml-7">
                      Experienced professional willing to guide
                    </p>
                  </button>
                </div>
              </div>

              {/* Interests - Only for Aspirants */}
              {selectedRole === "aspirant" && (
                <div className="space-y-4">
                  <Label className="text-sm font-medium text-secondary">What brings you here? (Select all that apply)</Label>
                  
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        id="lookingForJob"
                        type="checkbox"
                        checked={formData.lookingForJob}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData({
                            ...formData,
                            lookingForJob: checked,
                            justCommunityMember: checked ? false : formData.justCommunityMember,
                          });
                          if (error) setError('');
                        }}
                        className="h-4 w-4 text-primary focus:ring-primary/20 border-gray-300 rounded"
                      />
                      <label htmlFor="lookingForJob" className="text-sm font-medium text-secondary">
                        Job opportunities
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        id="upskilling"
                        type="checkbox"
                        checked={formData.upskilling}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData({
                            ...formData,
                            upskilling: checked,
                            justCommunityMember: checked ? false : formData.justCommunityMember,
                          });
                          if (error) setError('');
                        }}
                        className="h-4 w-4 text-primary focus:ring-primary/20 border-gray-300 rounded"
                      />
                      <label htmlFor="upskilling" className="text-sm font-medium text-secondary">
                        Upskilling / new tech
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      id="justCommunityMember"
                      type="radio"
                      name="membershipType"
                      checked={formData.justCommunityMember}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData({
                          ...formData,
                          justCommunityMember: checked,
                          lookingForJob: checked ? false : formData.lookingForJob,
                          upskilling: checked ? false : formData.upskilling,
                        });
                        if (error) setError('');
                      }}
                      className="h-4 w-4 text-primary focus:ring-primary/20 border-gray-300"
                    />
                    <label htmlFor="justCommunityMember" className="text-sm font-medium text-secondary">
                      Just want to be a member of the community
                    </label>
                  </div>
                  
                  <p className="text-xs text-gray-500">
                    💡 Selecting "Just community member" will focus your experience on networking and knowledge sharing
                  </p>
                </div>
              )}

              {/* Resume Upload — aspirants only, before contact details */}
              {selectedRole === "aspirant" && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-secondary">
                    Resume (PDF, DOC, DOCX — max 10MB) *
                  </Label>
                  <p className="text-xs text-gray-500">
                    Upload from device storage only (not Google Drive). Phone, address, and other details may be pre-filled from your resume — edit as needed.
                  </p>
                  <input
                    ref={resumeInputRef}
                    id="resume"
                    name="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => resumeInputRef.current?.click()}
                      className="h-11 shrink-0 border-border"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Browse files
                    </Button>
                    {formData.resume ? (
                      <p className="text-sm text-primary truncate">
                        ✓ {formData.resume.name}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">No file selected</p>
                    )}
                  </div>
                  {fieldErrors.resume && <p className="text-red-500 text-sm">{fieldErrors.resume}</p>}
                  {(resumePreviewAnalyzing || resumePreview || resumePreviewError) && (
                    <ResumeAnalysisBlurredPreview
                      analyzing={resumePreviewAnalyzing}
                      error={resumePreviewError}
                      analysis={resumePreview}
                      fileName={formData.resume?.name}
                      overlayMessage="Finish registration to unlock your full report"
                      subtitle="Your complete ATS breakdown, skills, and certification tips will be on your Profile page after sign-up."
                    />
                  )}

                  <div className="space-y-2 pt-1">
                    <Label htmlFor="primarySkills" className="text-sm font-medium text-secondary">
                      Primary Skills *
                    </Label>
                    <Input
                      id="primarySkills"
                      name="primarySkills"
                      type="text"
                      placeholder="e.g., Java, React, Financial Reporting, Accounting"
                      value={formData.primarySkills}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("primarySkills", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                      required
                    />
                    {fieldErrors.primarySkills && <p className="text-red-500 text-sm mt-1">{fieldErrors.primarySkills}</p>}
                    <p className="text-xs text-gray-500">
                      Suggested from your resume after upload — edit as needed. Separate skills with commas.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondarySkills" className="text-sm font-medium text-secondary">
                      Secondary Skills (Optional)
                    </Label>
                    <Input
                      id="secondarySkills"
                      name="secondarySkills"
                      type="text"
                      placeholder="e.g., Python, Node.js, Team Leadership, Project Management"
                      value={formData.secondarySkills}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("secondarySkills", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                    />
                    {fieldErrors.secondarySkills && <p className="text-red-500 text-sm mt-1">{fieldErrors.secondarySkills}</p>}
                    <p className="text-xs text-gray-500">Separate multiple skills with commas</p>
                  </div>
                </div>
              )}

              {/* Phone Field */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-secondary">
                  Phone Number *
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={(e) => handleFieldBlur("phone", e.target.value)}
                    className="pl-10 h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                    required
                  />
                  {fieldErrors.phone && <p className="text-red-500 text-sm mt-1">{fieldErrors.phone}</p>}
                </div>
              </div>

              {/* Address Fields */}
              <div className="space-y-4">
                <Label className="text-sm font-medium text-secondary">Address Information</Label>

                <div className="space-y-2">
                  <Label htmlFor="addressLine1" className="text-xs font-medium text-gray-600">
                    Address *
                  </Label>
                  <Input
                    id="addressLine1"
                    name="addressLine1"
                    type="text"
                    placeholder="Street address, apartment, area, house number"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    onBlur={(e) => handleFieldBlur("addressLine1", e.target.value)}
                    className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                    required
                  />
                  {fieldErrors.addressLine1 && <p className="text-red-500 text-xs mt-1">{fieldErrors.addressLine1}</p>}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pincode" className="text-xs font-medium text-gray-600">
                      Pincode *
                    </Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      type="text"
                      placeholder="6 digits"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("pincode", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                      maxLength={6}
                      inputMode="numeric"
                      required
                    />
                    {fieldErrors.pincode && <p className="text-red-500 text-xs mt-1">{fieldErrors.pincode}</p>}
                    {formData.pincode.replace(/\D/g, "").length === 6 && (
                      <p className="text-xs whitespace-nowrap overflow-hidden text-ellipsis">
                        {pincodeLookingUp && <span className="text-gray-500">Looking up city, state & country…</span>}
                        {!pincodeLookingUp && !pincodeLookupFailed && (
                          <span className="text-green-600">City, state & country filled from pincode</span>
                        )}
                        {!pincodeLookingUp && pincodeLookupFailed && (
                          <span className="text-amber-600">Pincode not found — enter city, state & country manually</span>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-xs font-medium text-gray-600">
                      City *
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      placeholder="Enter city"
                      value={formData.city}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("city", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                      required
                    />
                    {fieldErrors.city && <p className="text-red-500 text-xs mt-1">{fieldErrors.city}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-xs font-medium text-gray-600">
                      State *
                    </Label>
                    <Input
                      id="state"
                      name="state"
                      type="text"
                      placeholder="Enter state"
                      value={formData.state}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("state", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                      required
                    />
                    {fieldErrors.state && <p className="text-red-500 text-xs mt-1">{fieldErrors.state}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-xs font-medium text-gray-600">
                      Country *
                    </Label>
                    <Input
                      id="country"
                      name="country"
                      type="text"
                      placeholder="Enter country"
                      value={formData.country}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("country", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                      required
                    />
                    {fieldErrors.country && <p className="text-red-500 text-xs mt-1">{fieldErrors.country}</p>}
                  </div>
                </div>
              </div>

              {/* Skills for Mentors */}
              {selectedRole === "mentor" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="primarySkills" className="text-sm font-medium text-secondary">
                      Primary Skills *
                    </Label>
                    <Input
                      id="primarySkills"
                      name="primarySkills"
                      type="text"
                      placeholder="e.g., Java, React, Financial Reporting, Accounting"
                      value={formData.primarySkills}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("primarySkills", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                      required
                    />
                    {fieldErrors.primarySkills && <p className="text-red-500 text-sm mt-1">{fieldErrors.primarySkills}</p>}
                    <p className="text-xs text-gray-500">Separate multiple skills with commas</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secondarySkills" className="text-sm font-medium text-secondary">
                      Secondary Skills (Optional)
                    </Label>
                    <Input
                      id="secondarySkills"
                      name="secondarySkills"
                      type="text"
                      placeholder="e.g., Python, Node.js, Team Leadership"
                      value={formData.secondarySkills}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("secondarySkills", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                    />
                    {fieldErrors.secondarySkills && <p className="text-red-500 text-sm mt-1">{fieldErrors.secondarySkills}</p>}
                  </div>
                </>
              )}

              {/* Demographic Fields — after skills */}
              {selectedRole && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth" className="text-sm font-medium text-secondary">
                      Date of Birth *
                    </Label>
                    <Input
                      id="dateOfBirth"
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur("dateOfBirth", e.target.value)}
                      className="h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                      required
                    />
                    {fieldErrors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{fieldErrors.dateOfBirth}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-secondary">Gender *</Label>
                    <div className="flex h-11 rounded-lg border border-border overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, gender: "male" });
                          handleFieldBlur("gender", "male");
                        }}
                        className={`flex-1 text-sm font-medium transition-colors ${
                          formData.gender === "male"
                            ? "bg-primary text-white"
                            : "bg-white text-secondary hover:bg-gray-50"
                        }`}
                      >
                        Male
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, gender: "female" });
                          handleFieldBlur("gender", "female");
                        }}
                        className={`flex-1 text-sm font-medium transition-colors border-l border-border ${
                          formData.gender === "female"
                            ? "bg-primary text-white"
                            : "bg-white text-secondary hover:bg-gray-50"
                        }`}
                      >
                        Female
                      </button>
                    </div>
                    {fieldErrors.gender && <p className="text-red-500 text-sm mt-1">{fieldErrors.gender}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-secondary">Marital Status *</Label>
                    <div className="flex h-11 rounded-lg border border-border overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, maritalStatus: "married" });
                          handleFieldBlur("maritalStatus", "married");
                        }}
                        className={`flex-1 text-sm font-medium transition-colors ${
                          formData.maritalStatus === "married"
                            ? "bg-primary text-white"
                            : "bg-white text-secondary hover:bg-gray-50"
                        }`}
                      >
                        Married
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, maritalStatus: "unmarried" });
                          handleFieldBlur("maritalStatus", "unmarried");
                        }}
                        className={`flex-1 text-sm font-medium transition-colors border-l border-border ${
                          formData.maritalStatus === "unmarried"
                            ? "bg-primary text-white"
                            : "bg-white text-secondary hover:bg-gray-50"
                        }`}
                      >
                        Unmarried
                      </button>
                    </div>
                    {fieldErrors.maritalStatus && <p className="text-red-500 text-sm mt-1">{fieldErrors.maritalStatus}</p>}
                  </div>
                </div>
              )}

              {/* Submit */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {error}
                  {isEmailNotVerified && (
                    <div className="mt-2">
                      <Link 
                        to="/login" 
                        className="inline-flex items-center text-primary hover:text-primary-dark font-medium underline"
                      >
                        Go to Login Page →
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-md text-sm">
                  {success}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={
                  loading ||
                  (!linkedinConnected && !usePassword) ||
                  !selectedRole ||
                  (selectedRole === "aspirant" && !formData.lookingForJob && !formData.upskilling && !formData.justCommunityMember)
                }
                className="w-full h-11 bg-primary hover:bg-primary-dark text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  "Create Account"
                )}
              </Button>

              <p className="text-xs text-center text-gray-600">
                By creating an account, you agree to our{" "}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark underline"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark underline"
                >
                  Privacy Policy
                </a>
                .
              </p>

              {/* Login Link */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="font-medium text-primary hover:text-primary-dark transition-colors"
                  >
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 